import React, { useState, useEffect } from 'react';
import { X, Trash2, Settings } from 'lucide-react';

// Property configuration for each node type
const nodeProperties = {
  // Start & Input
  start: [],
  document_upload: [
    { key: 'accept', label: 'Types de fichiers acceptés', type: 'text', default: '.pdf,.doc,.docx,.txt' },
    { key: 'maxSize', label: 'Taille max (Mo)', type: 'number', default: 10 },
  ],
  manual_input: [
    { key: 'fieldLabel', label: 'Libellé du champ', type: 'text', default: 'Saisir le texte' },
    { key: 'fieldType', label: 'Type de champ', type: 'select', options: ['text', 'textarea', 'number', 'date'], default: 'text' },
  ],
  api_trigger: [
    { key: 'endpoint', label: 'Endpoint API', type: 'text', default: '' },
    { key: 'method', label: 'Méthode HTTP', type: 'select', options: ['GET', 'POST', 'PUT', 'DELETE'], default: 'POST' },
  ],
  schedule: [
    { key: 'frequency', label: 'Fréquence', type: 'select', options: ['daily', 'weekly', 'monthly'], default: 'daily' },
    { key: 'time', label: 'Heure', type: 'text', default: '09:00' },
  ],

  // Document Analysis
  document_analysis: [
    { key: 'name', label: 'Nom de l\'analyse', type: 'text', default: 'Analyse standard' },
    { key: 'analysisType', label: 'Type d\'analyse', type: 'select', options: ['standard', 'approfondie', 'risques', 'clauses'], default: 'standard' },
    { key: 'extractParties', label: 'Extraire les parties', type: 'checkbox', default: true },
    { key: 'extractDates', label: 'Extraire les dates', type: 'checkbox', default: true },
    { key: 'extractAmounts', label: 'Extraire les montants', type: 'checkbox', default: true },
  ],
  document_comparison: [
    { key: 'name', label: 'Nom de la comparaison', type: 'text', default: 'Comparaison de documents' },
    { key: 'compareMode', label: 'Mode de comparaison', type: 'select', options: ['textuelle', 'sémantique', 'structurelle'], default: 'textuelle' },
    { key: 'highlightChanges', label: 'Surligner les changements', type: 'checkbox', default: true },
  ],
  bulk_processing: [
    { key: 'name', label: 'Nom du traitement', type: 'text', default: 'Traitement en masse' },
    { key: 'analysisType', label: 'Type d\'analyse', type: 'select', options: ['standard', 'comparative', 'due_diligence', 'custom'], default: 'standard' },
    { key: 'maxDocuments', label: 'Nombre max de documents', type: 'number', default: 40 },
  ],
  ocr: [
    { key: 'language', label: 'Langue', type: 'select', options: ['fra', 'eng', 'auto'], default: 'fra' },
  ],
  document_classification: [
    { key: 'categories', label: 'Catégories (séparées par virgule)', type: 'text', default: 'Contrat, Facture, Courrier' },
  ],
  risk_assessment: [
    { key: 'riskLevel', label: 'Niveau de sensibilité', type: 'select', options: ['élevé', 'moyen', 'faible'], default: 'moyen' },
  ],

  // Contracts
  contract_drafting: [
    { key: 'name', label: 'Nom du contrat', type: 'text', default: 'Nouveau contrat' },
    { key: 'template', label: 'Modèle', type: 'select', options: ['NDA', 'CDI', 'Prestation', 'Bail', 'Vente', 'Freelance', 'Partenariat', 'Cession', 'Licence', 'Mandat'], default: 'NDA' },
    { key: 'language', label: 'Langue', type: 'select', options: ['Français', 'English'], default: 'Français' },
  ],
  contract_review: [
    { key: 'name', label: 'Nom de la révision', type: 'text', default: 'Révision de contrat' },
    { key: 'focusAreas', label: 'Points d\'attention', type: 'text', default: 'Clauses de résiliation, Obligations' },
  ],
  clause_extraction: [
    { key: 'clauseTypes', label: 'Types de clauses', type: 'text', default: 'Confidentialité, Résiliation, Responsabilité' },
  ],
  clause_improvement: [
    { key: 'improvementType', label: 'Type d\'amélioration', type: 'select', options: ['clarté', 'légalité', 'protection'], default: 'clarté' },
  ],
  template_selection: [
    { key: 'domain', label: 'Domaine juridique', type: 'select', options: ['Travail', 'Commercial', 'Immobilier', 'Propriété intellectuelle'], default: 'Commercial' },
  ],

  // AI & Research
  legal_chat: [
    { key: 'name', label: 'Nom de la requête', type: 'text', default: 'Question juridique' },
    { key: 'mode', label: 'Mode', type: 'select', options: ['standard', 'audit_strict'], default: 'standard' },
    { key: 'prompt', label: 'Question', type: 'textarea', default: '' },
  ],
  legal_research: [
    { key: 'name', label: 'Nom de la recherche', type: 'text', default: 'Recherche légale' },
    { key: 'sources', label: 'Sources', type: 'multiselect', options: ['Légifrance', 'EUR-Lex', 'Jurisprudence'], default: ['Légifrance'] },
    { key: 'query', label: 'Requête de recherche', type: 'textarea', default: '' },
  ],
  compliance_check: [
    { key: 'name', label: 'Nom de la vérification', type: 'text', default: 'Vérification conformité' },
    { key: 'regulation', label: 'Règlementation', type: 'select', options: ['RGPD', 'Code du travail', 'Code de commerce', 'Autre'], default: 'RGPD' },
  ],
  due_diligence: [
    { key: 'checklistType', label: 'Type de checklist', type: 'select', options: ['Standard', 'Acquisition', 'Investissement'], default: 'Standard' },
  ],
  precedent_search: [
    { key: 'jurisdiction', label: 'Juridiction', type: 'select', options: ['Cour de cassation', 'Conseil d\'\u00c9tat', 'Cours d\'appel', 'Toutes'], default: 'Toutes' },
    { key: 'keywords', label: 'Mots-clés', type: 'text', default: '' },
  ],

  // Legal Watch
  legal_watch: [
    { key: 'name', label: 'Nom de la veille', type: 'text', default: 'Veille légale' },
    { key: 'domains', label: 'Domaines', type: 'multiselect', options: ['Droit du travail', 'Droit des contrats', 'Droit commercial', 'RGPD', 'Droit fiscal', 'Droit civil'], default: [] },
  ],
  filter_domain: [
    { key: 'domain', label: 'Domaine à filtrer', type: 'select', options: ['Droit du travail', 'Droit des contrats', 'Droit commercial', 'RGPD', 'Droit fiscal', 'Droit civil'], default: 'RGPD' },
  ],
  alert_changes: [
    { key: 'alertChannel', label: 'Canal d\'alerte', type: 'select', options: ['Email', 'Notification', 'Les deux'], default: 'Les deux' },
  ],
  impact_analysis: [
    { key: 'impactLevel', label: 'Niveau d\'impact minimum', type: 'select', options: ['faible', 'moyen', 'élevé'], default: 'moyen' },
  ],

  // Collaboration
  add_comment: [
    { key: 'commentText', label: 'Texte du commentaire', type: 'textarea', default: '' },
    { key: 'position', label: 'Position (page:ligne)', type: 'text', default: '1:1' },
  ],
  request_approval: [
    { key: 'approverEmail', label: 'Email de l\'approbateur', type: 'text', default: '' },
    { key: 'message', label: 'Message', type: 'textarea', default: '' },
  ],
  send_notification: [
    { key: 'recipient', label: 'Destinataire', type: 'text', default: '' },
    { key: 'subject', label: 'Sujet', type: 'text', default: '' },
    { key: 'message', label: 'Message', type: 'textarea', default: '' },
  ],
  assign_task: [
    { key: 'assignee', label: 'Assigné à', type: 'text', default: '' },
    { key: 'taskDescription', label: 'Description de la tâche', type: 'textarea', default: '' },
    { key: 'dueDate', label: 'Date d\'\u00e9chéance', type: 'date', default: '' },
  ],
  share_document: [
    { key: 'shareWith', label: 'Partager avec (emails séparés par virgule)', type: 'text', default: '' },
    { key: 'permission', label: 'Permission', type: 'select', options: ['Lecture', 'Édition', 'Admin'], default: 'Lecture' },
  ],

  // Logic & Control
  condition: [
    { key: 'field', label: 'Champ à tester', type: 'text', default: '' },
    { key: 'operator', label: 'Opérateur', type: 'select', options: ['equals', 'not_equals', 'contains', 'greater_than', 'less_than'], default: 'equals' },
    { key: 'value', label: 'Valeur', type: 'text', default: '' },
  ],
  loop: [
    { key: 'iterateOver', label: 'Itérer sur', type: 'text', default: 'documents' },
    { key: 'maxIterations', label: 'Nombre max d\'itérations', type: 'number', default: 100 },
  ],
  delay: [
    { key: 'duration', label: 'Durée (secondes)', type: 'number', default: 60 },
  ],
  merge: [],
  split: [
    { key: 'splitBy', label: 'Diviser par', type: 'select', options: ['condition', 'round_robin', 'random'], default: 'condition' },
  ],

  // Output & Export
  export_document: [
    { key: 'format', label: 'Format', type: 'select', options: ['PDF', 'DOCX', 'TXT', 'HTML'], default: 'PDF' },
    { key: 'filename', label: 'Nom du fichier', type: 'text', default: 'document' },
  ],
  send_email: [
    { key: 'to', label: 'Destinataire (to) — ex: {{client_email}}', type: 'text', default: '' },
    { key: 'subject', label: 'Objet', type: 'text', default: '' },
    { key: 'body', label: 'Corps (HTML) — variables {{var}} supportées', type: 'textarea', default: '' },
  ],
  save_database: [
    { key: 'collection', label: 'Collection', type: 'text', default: 'documents' },
  ],
  generate_report: [
    { key: 'reportType', label: 'Type de rapport', type: 'select', options: ['Synthèse', 'Détaillé', 'Statistiques'], default: 'Synthèse' },
  ],
  end: [],
};

const PropertiesPanel = ({ node, onUpdate, onDelete, onClose }) => {
  const [config, setConfig] = useState(node.data.config || {});
  const properties = nodeProperties[node.data.nodeType] || [];

  useEffect(() => {
    setConfig(node.data.config || {});
  }, [node]);

  const handleChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    onUpdate(node.id, newConfig);
  };

  const renderField = (property) => {
    const value = config[property.key] || property.default;

    switch (property.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(property.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(property.key, parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleChange(property.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(property.key, e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(property.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {property.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => handleChange(property.key, e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Activé</span>
          </label>
        );

      case 'multiselect':
        return (
          <div className="space-y-2">
            {property.options.map((option) => (
              <label key={option} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={(value || []).includes(option)}
                  onChange={(e) => {
                    const currentValues = value || [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter((v) => v !== option);
                    handleChange(property.key, newValues);
                  }}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-gray-700" />
          <h3 className="font-bold text-gray-900">Propriétés</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Node Info */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundColor: `${node.data.color}20` }}
          >
            <div className="w-4 h-4" style={{ color: node.data.color }}>•</div>
          </div>
          <div>
            <div className="font-semibold text-gray-900">{node.data.label}</div>
            <div className="text-xs text-gray-500">ID: {node.id}</div>
          </div>
        </div>
      </div>

      {/* Properties Form */}
      <div className="flex-1 overflow-y-auto p-4">
        {properties.length > 0 ? (
          <div className="space-y-4">
            {properties.map((property) => (
              <div key={property.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {property.label}
                </label>
                {renderField(property)}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 mt-8">
            <p className="text-sm">Aucune propriété configurable pour ce nœud.</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={onDelete}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Supprimer le nœud
        </button>
      </div>
    </div>
  );
};

export default PropertiesPanel;
